import {
  buildTolerantProjectParameterMutationBatches,
  findAuthoritativeProjectParameterBindingKind,
  type AvailableProjectParameterDescriptor,
  type ParameterBindingKind,
  type ParameterTypeHint,
} from '../../revit/projectParameterMutationPlan';
import { buildTextParameterTypeHints } from '../../revit/wizardParameterDataTypes';
import {
  buildEquivalentRevitParameterNameLookup,
  getEquivalentRevitParameterNames,
  sanitizeRevitParameterNames,
} from '../../revitParameterNames';
import { groupParameterEntriesByBindingKind } from './parameterBindingGrouping';

export interface ProcessProjectParameterBindingGroup {
  parameterNames: string[];
  bindingKind: ParameterBindingKind;
}

export interface ProcessProjectParameterRequest
  extends ProcessProjectParameterBindingGroup {
  categories: string[];
}

export interface ProcessProjectParameterMutationRequest
  extends ProcessProjectParameterRequest {
  parameterTypeHints: ParameterTypeHint[];
}

export function buildProcessProjectParameterBindingGroups(input: {
  parameterNames: string[];
  connectorParameterNames: string[];
  connectorBindingKind: ParameterBindingKind;
}): ProcessProjectParameterBindingGroup[] {
  const parameterNames = sanitizeRevitParameterNames(input.parameterNames);
  if (parameterNames.length === 0) {
    return [];
  }

  const connectorNames = buildEquivalentRevitParameterNameLookup(
    input.connectorParameterNames
  );
  return groupParameterEntriesByBindingKind(
    parameterNames.map((parameterName) => ({
      parameterName,
      bindingKind: getEquivalentRevitParameterNames(parameterName).some((name) =>
        connectorNames.has(name.toLowerCase())
      ) ? input.connectorBindingKind : 'instance',
    }))
  );
}

export function buildProcessProjectParameterMutationRequests(
  requests: ProcessProjectParameterRequest[],
  availableParameters: AvailableProjectParameterDescriptor[]
): ProcessProjectParameterMutationRequest[] {
  return requests.flatMap((request) =>
    adoptRequestBindingKinds(request, availableParameters).flatMap((group) =>
      buildTolerantProjectParameterMutationBatches(
        group.parameterNames,
        group.bindingKind,
        request.categories,
        availableParameters,
        buildTextParameterTypeHints(group.parameterNames)
      ).map((batch) => ({
        parameterNames: batch.parameterNames,
        categories: request.categories,
        bindingKind: batch.bindingKind,
        parameterTypeHints: buildTextParameterTypeHints(batch.parameterNames),
      }))
    )
  );
}

function adoptRequestBindingKinds(
  request: ProcessProjectParameterRequest,
  availableParameters: AvailableProjectParameterDescriptor[]
): ProcessProjectParameterBindingGroup[] {
  const hints = buildTextParameterTypeHints(request.parameterNames);
  return groupParameterEntriesByBindingKind(
    request.parameterNames.map((parameterName) => ({
      parameterName,
      bindingKind:
        findAuthoritativeProjectParameterBindingKind(
          parameterName,
          availableParameters,
          hints
        ) ?? request.bindingKind,
    }))
  );
}
